"use client";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@devhop/ui";
import { useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import type { CreateShopPayoutRequestDTO } from "@/modules/merchant/domain/contracts";
import {
  useCreateShopPayoutRequestMutation,
  useShopPayoutRequestsQuery,
  useShopSettlementPeriodsQuery,
  useShopMarketplaceSettlementPeriodsQuery,
  useShopSettlementSummaryQuery,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { SHOP_CURRENCIES, formatShopMoney } from "@/shared/lib/currency";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

function formatDt(iso: string) {
  try {
    return new Date(iso).toLocaleString("lo-LA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function settlementStatusBadge(status: string) {
  if (status === "paid_out" || status === "settled")
    return (
      <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
        {status === "settled" ? "ຊຳລະແລ້ວ" : "ຈ່າຍແລ້ວ"}
      </Badge>
    );
  if (status === "processing")
    return (
      <Badge variant="secondary" className="bg-amber-100 text-amber-900">
        ກຳລັງປະມວນຜົນ
      </Badge>
    );
  if (status === "failed")
    return <Badge variant="destructive">ລົ້ມເຫຼວ</Badge>;
  return <Badge variant="outline">ລໍຖ້າ</Badge>;
}

function payoutStatusBadge(status: string) {
  if (status === "paid")
    return (
      <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
        ຈ່າຍແລ້ວ
      </Badge>
    );
  if (status === "failed" || status === "rejected")
    return <Badge variant="destructive">ບໍ່ສຳເລັດ</Badge>;
  if (status === "processing" || status === "approved")
    return <Badge variant="secondary">ດຳເນີນການ</Badge>;
  return <Badge variant="outline">ຮ້ອງຂໍ</Badge>;
}

const LIMIT = 20;

export function ShopSettlementPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentShop } = useMerchantShops(shopId);
  const verified = currentShop?.verifiedLevel ?? null;
  const perms = currentShop?.permissions ?? [];

  const canRequestPayout = canUseMerchantPermission(
    perms,
    MerchantPermissions.settlement.requestPayout,
    verified,
  );

  const [periodOffset, setPeriodOffset] = useState(0);
  const [mpOffset, setMpOffset] = useState(0);
  const [payoutOffset, setPayoutOffset] = useState(0);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState<{
    amount: string;
    currency: CreateShopPayoutRequestDTO["currency"];
    payoutMethod: CreateShopPayoutRequestDTO["payoutMethod"];
    bankName: string;
    accountNumber: string;
    accountName: string;
  }>({
    amount: "",
    currency: "LAK",
    payoutMethod: "bank_transfer",
    bankName: "",
    accountNumber: "",
    accountName: "",
  });

  const summaryQ = useShopSettlementSummaryQuery(shopId);
  const periodsQ = useShopSettlementPeriodsQuery(shopId, {
    limit: LIMIT,
    offset: periodOffset,
  });
  const mpPeriodsQ = useShopMarketplaceSettlementPeriodsQuery(shopId, {
    limit: LIMIT,
    offset: mpOffset,
  });
  const payoutQ = useShopPayoutRequestsQuery(shopId, {
    limit: LIMIT,
    offset: payoutOffset,
  });
  const createPayout = useCreateShopPayoutRequestMutation(shopId);

  const periodMeta = periodsQ.data?.meta;
  const periodTotal = periodMeta?.total ?? 0;
  const hasPrevPeriod = periodOffset > 0;
  const hasNextPeriod = periodOffset + LIMIT < periodTotal;

  const mpMeta = mpPeriodsQ.data?.meta;
  const mpTotal = mpMeta?.total ?? 0;
  const hasPrevMp = mpOffset > 0;
  const hasNextMp = mpOffset + LIMIT < mpTotal;

  const payoutMeta = payoutQ.data?.meta;
  const payoutTotal = payoutMeta?.total ?? 0;
  const hasPrevPayout = payoutOffset > 0;
  const hasNextPayout = payoutOffset + LIMIT < payoutTotal;

  const byCurrency = summaryQ.data?.byCurrency;
  const mpByCurrency = summaryQ.data?.marketplace?.byCurrency;
  const wallets = summaryQ.data?.wallets ?? [];

  const walletByCurrency = useMemo(() => {
    const m = new Map(
      wallets.map((w) => [w.currency, w] as const),
    );
    return m;
  }, [wallets]);

  const submitPayout = () => {
    const amount = Number(payoutForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("ກະລຸນາໃສ່ຈຳນວນເງິນ");
      return;
    }
    const body: CreateShopPayoutRequestDTO = {
      amount,
      currency: payoutForm.currency,
      payoutMethod: payoutForm.payoutMethod,
    };
    if (payoutForm.payoutMethod === "bank_transfer") {
      const d = {
        bankName: payoutForm.bankName.trim() || undefined,
        accountNumber: payoutForm.accountNumber.trim() || undefined,
        accountName: payoutForm.accountName.trim() || undefined,
      };
      if (d.bankName || d.accountNumber || d.accountName) {
        body.payoutDestination = d;
      }
    }
    createPayout.mutate(body, {
      onSuccess: () => {
        toast.success("ສົ່ງຄຳຂໍຮັບເງິນແລ້ວ");
        setPayoutOpen(false);
        setPayoutForm((f) => ({
          ...f,
          amount: "",
          bankName: "",
          accountNumber: "",
          accountName: "",
        }));
      },
      onError: (e: unknown) => {
        toast.error(e instanceof Error ? e.message : "ບໍ່ສຳເລັດ");
      },
    });
  };

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.settlement.read}
      fallback={
        <div className="p-6 text-muted-foreground">
          ທ່ານບໍ່ມີສິດເບິ່ງຫນ້າ settlement ຂອງຮ້ານນີ້
        </div>
      }
    >
      <div className="min-h-0 min-w-0 space-y-4">
        <Header>
          <Breadcrumb
            className="mb-2"
            items={shopBreadcrumbs.settlement(shopId)}
          />
        </Header>
        <Main className="mt-0 space-y-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                ຊຳລະ / Settlement
              </h1>
              <p className="text-muted-foreground text-sm">
                ຍອດຕາມຮອບຈາກ POS + Marketplace + ປະຫວັດຂໍຮັບເງິນ (wallet ຮ້ານ)
              </p>
            </div>
            {canRequestPayout && (
              <Button type="button" onClick={() => setPayoutOpen(true)}>
                ຂໍຮັບເງິນ
              </Button>
            )}
          </div>

          {summaryQ.isError && (
            <p className="text-destructive text-sm">
              ໂຫລດສະຫຼຸບບໍ່ສຳເລັດ — ລອງໃໝ່
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SHOP_CURRENCIES.map((cur) => {
              const b = byCurrency?.[cur];
              const mp = mpByCurrency?.[cur];
              const w = walletByCurrency.get(cur);
              return (
                <Card key={cur}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      {cur}{" "}
                      {w ? (
                        <span className="text-muted-foreground font-normal text-sm">
                          · wallet {w.status}
                        </span>
                      ) : (
                        <span className="text-amber-700 text-sm">
                          · ຍັງບໍ່ມີ wallet
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ລໍຖ້າ</span>
                      <span>
                        {formatShopMoney(b?.pending ?? 0, cur)} {cur}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ກຳລັງປະມວນ</span>
                      <span>
                        {formatShopMoney(b?.processing ?? 0, cur)} {cur}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ຈ່າຍອອກແລ້ວ</span>
                      <span>
                        {formatShopMoney(b?.paid_out ?? 0, cur)} {cur}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ລົ້ມເຫຼວ</span>
                      <span>
                        {formatShopMoney(b?.failed ?? 0, cur)} {cur}
                      </span>
                    </div>
                    {mp && (
                      <div className="mt-2 border-t pt-2">
                        <p className="mb-1 text-muted-foreground text-xs">
                          Marketplace (net)
                        </p>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">settled</span>
                          <span>
                            {formatShopMoney(mp.settled ?? 0, cur)} {cur}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">ລໍຖ້າ</span>
                          <span>
                            {formatShopMoney(mp.pending ?? 0, cur)} {cur}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>ຮອບ settlement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {periodsQ.isLoading && (
                <p className="text-muted-foreground text-sm">ກຳລັງໂຫລດ…</p>
              )}
              {!periodsQ.isLoading &&
                (periodsQ.data?.data.length ?? 0) === 0 && (
                  <p className="text-muted-foreground text-sm">
                    ຍັງບໍ່ມີຮອບ settlement ໃນລະບົບ — ລໍຖ້າ job ສ້າງຮອບຈາກການຂາຍ wallet
                  </p>
                )}
              {(periodsQ.data?.data.length ?? 0) > 0 && (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 font-medium">ສະກຸນ</th>
                        <th className="p-2 font-medium">ຍອດ</th>
                        <th className="p-2 font-medium">ສະຖານະ</th>
                        <th className="p-2 font-medium">ຊ່ວງຮອບ</th>
                        <th className="p-2 font-medium">ສ້າງເມື່ອ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodsQ.data?.data.map((row) => (
                        <tr key={row.id} className="border-t">
                          <td className="p-2">{row.currency}</td>
                          <td className="p-2">
                            {formatShopMoney(
                              row.totalAmount,
                              row.currency as (typeof SHOP_CURRENCIES)[number],
                            )}{" "}
                            {row.currency}
                          </td>
                          <td className="p-2">
                            {settlementStatusBadge(row.status)}
                          </td>
                          <td className="p-2 text-xs text-muted-foreground">
                            {formatDt(row.periodStart)} —{" "}
                            {formatDt(row.periodEnd)}
                          </td>
                          <td className="p-2 text-xs">
                            {formatDt(row.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {periodMeta && periodTotal > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!hasPrevPeriod}
                    onClick={() =>
                      setPeriodOffset((o) => Math.max(0, o - LIMIT))
                    }
                  >
                    ກ່ອນໜ້າ
                  </Button>
                  <span className="text-muted-foreground text-sm">
                    {periodOffset + 1}–
                    {Math.min(periodOffset + LIMIT, periodTotal)} /{" "}
                    {periodTotal}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!hasNextPeriod}
                    onClick={() => setPeriodOffset((o) => o + LIMIT)}
                  >
                    ຖັດໄປ
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Marketplace — settlement ຕໍ່ຄຳສັ່ງ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {mpPeriodsQ.isLoading && (
                <p className="text-muted-foreground text-sm">ກຳລັງໂຫລດ…</p>
              )}
              {!mpPeriodsQ.isLoading &&
                (mpPeriodsQ.data?.data.length ?? 0) === 0 && (
                  <p className="text-muted-foreground text-sm">
                    ຍັງບໍ່ມີຄຳສັ່ງ marketplace ທີ່ settle ແລ້ວ
                  </p>
                )}
              {(mpPeriodsQ.data?.data.length ?? 0) > 0 && (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full min-w-[800px] text-left text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 font-medium">ຄຳສັ່ງ</th>
                        <th className="p-2 font-medium">ຍອດລວມ</th>
                        <th className="p-2 font-medium">ຄ່າຄອມ</th>
                        <th className="p-2 font-medium">net</th>
                        <th className="p-2 font-medium">ສະຖານະ</th>
                        <th className="p-2 font-medium">ເມື່ອ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mpPeriodsQ.data?.data.map((row) => (
                        <tr key={row.id} className="border-t">
                          <td className="p-2 font-mono text-xs">
                            {row.orderId.slice(0, 8)}…
                          </td>
                          <td className="p-2">
                            {formatShopMoney(
                              row.grossAmount,
                              row.currency as (typeof SHOP_CURRENCIES)[number],
                            )}{" "}
                            {row.currency}
                          </td>
                          <td className="p-2">
                            {formatShopMoney(
                              row.commissionAmount,
                              row.currency as (typeof SHOP_CURRENCIES)[number],
                            )}
                          </td>
                          <td className="p-2">
                            {formatShopMoney(
                              row.netAmount,
                              row.currency as (typeof SHOP_CURRENCIES)[number],
                            )}
                          </td>
                          <td className="p-2">
                            {settlementStatusBadge(row.status)}
                          </td>
                          <td className="p-2 text-xs">
                            {formatDt(row.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {mpMeta && mpTotal > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!hasPrevMp}
                    onClick={() => setMpOffset((o) => Math.max(0, o - LIMIT))}
                  >
                    ກ່ອນໜ້າ
                  </Button>
                  <span className="text-muted-foreground text-sm">
                    {mpOffset + 1}–{Math.min(mpOffset + LIMIT, mpTotal)} /{" "}
                    {mpTotal}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!hasNextMp}
                    onClick={() => setMpOffset((o) => o + LIMIT)}
                  >
                    ຖັດໄປ
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ຄຳຂໍຮັບເງິນ (payout)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {payoutQ.isLoading && (
                <p className="text-muted-foreground text-sm">ກຳລັງໂຫລດ…</p>
              )}
              {!payoutQ.isLoading && wallets.length === 0 && (
                <p className="text-muted-foreground text-sm">
                  ຍັງບໍ່ມີ wallet ສຳລັບຮ້ານ — ບໍ່ສາມາດສະແດງປະຫວັດຂໍຈ່າຍ
                </p>
              )}
              {!payoutQ.isLoading &&
                wallets.length > 0 &&
                (payoutQ.data?.data.length ?? 0) === 0 && (
                  <p className="text-muted-foreground text-sm">
                    ຍັງບໍ່ມີຄຳຂໍຮັບເງິນ
                  </p>
                )}
              {(payoutQ.data?.data.length ?? 0) > 0 && (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 font-medium">ສະກຸນ</th>
                        <th className="p-2 font-medium">ຈຳນວນ</th>
                        <th className="p-2 font-medium">ວິທີ</th>
                        <th className="p-2 font-medium">ສະຖານະ</th>
                        <th className="p-2 font-medium">ສ້າງເມື່ອ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutQ.data?.data.map((row) => (
                        <tr key={row.id} className="border-t">
                          <td className="p-2">{row.currency}</td>
                          <td className="p-2">
                            {formatShopMoney(row.amount, row.currency)}{" "}
                            {row.currency}
                          </td>
                          <td className="p-2 text-xs">
                            {row.payoutMethod === "bank_transfer"
                              ? "ໂອນ"
                              : "ເງິນສົດ"}
                          </td>
                          <td className="p-2">
                            {payoutStatusBadge(row.status)}
                          </td>
                          <td className="p-2 text-xs">
                            {formatDt(row.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {payoutMeta && payoutTotal > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!hasPrevPayout}
                    onClick={() =>
                      setPayoutOffset((o) => Math.max(0, o - LIMIT))
                    }
                  >
                    ກ່ອນໜ້າ
                  </Button>
                  <span className="text-muted-foreground text-sm">
                    {payoutOffset + 1}–
                    {Math.min(payoutOffset + LIMIT, payoutTotal)} / {payoutTotal}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!hasNextPayout}
                    onClick={() => setPayoutOffset((o) => o + LIMIT)}
                  >
                    ຖັດໄປ
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </Main>
      </div>

      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ຂໍຮັບເງິນ</DialogTitle>
            <DialogDescription>
              ສົ່ງຄຳຂໍຈາກ wallet ຕາມສະກຸນ — ການອະນຸມັດຢູ່ຝັ່ງຜູ້ບໍລິຫານ
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>ສະກຸນ</Label>
              <SimpleSelect
                value={payoutForm.currency}
                onValueChange={(v) =>
                  setPayoutForm((f) => ({
                    ...f,
                    currency: v as CreateShopPayoutRequestDTO["currency"],
                  }))
                }
                options={SHOP_CURRENCIES.map((c) => ({ value: c, label: c }))}
                placeholder="ເລືອກສະກຸນ"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="payout-amount">ຈຳນວນ</Label>
              <Input
                id="payout-amount"
                inputMode="decimal"
                value={payoutForm.amount}
                onChange={(e) =>
                  setPayoutForm((f) => ({ ...f, amount: e.target.value }))
                }
                placeholder="0"
              />
            </div>
            <div className="space-y-1.5">
              <Label>ວິທີ</Label>
              <SimpleSelect
                value={payoutForm.payoutMethod}
                onValueChange={(v) =>
                  setPayoutForm((f) => ({
                    ...f,
                    payoutMethod: v as CreateShopPayoutRequestDTO["payoutMethod"],
                  }))
                }
                options={[
                  { value: "bank_transfer", label: "ໂອນເຂົ້າທະນາຄານ" },
                  { value: "cash", label: "ເງິນສົດ" },
                ]}
                placeholder="ເລືອກ"
              />
            </div>
            {payoutForm.payoutMethod === "bank_transfer" && (
              <div className="space-y-2 rounded-md border p-3">
                <p className="text-muted-foreground text-xs">
                  ຂໍ້ມູນບັນຊີ (ຖ້າມີ)
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="bn">ທະນາຄານ</Label>
                  <Input
                    id="bn"
                    value={payoutForm.bankName}
                    onChange={(e) =>
                      setPayoutForm((f) => ({
                        ...f,
                        bankName: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="anum">ເລກບັນຊີ</Label>
                  <Input
                    id="anum"
                    value={payoutForm.accountNumber}
                    onChange={(e) =>
                      setPayoutForm((f) => ({
                        ...f,
                        accountNumber: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="aname">ຊື່ບັນຊີ</Label>
                  <Input
                    id="aname"
                    value={payoutForm.accountName}
                    onChange={(e) =>
                      setPayoutForm((f) => ({
                        ...f,
                        accountName: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPayoutOpen(false)}
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="button"
                disabled={createPayout.isPending}
                onClick={submitPayout}
              >
                {createPayout.isPending ? "ກຳລັງສົ່ງ…" : "ສົ່ງຄຳຂໍ"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </RequireMerchantPermission>
  );
}
